"use client";
import { WelcomeModalDisplay } from "@/components/scanToOrder/runningTabsWelcomeModal/welcomeModalDisplay";
import { DayPart } from "@/dto/dayPartMenu";
import { Item } from "@/dto/item";
import { Menu } from "@/dto/menu";
import { Merchant } from "@/dto/merchant";
import { TableData, TableError } from "@/dto/tableData";
import { cloverColorTheme } from "@/utils/cloverColorTheme";
import {
  ROUTE_CATEGORIES,
  ROUTE_MENU,
  ROUTE_NOT_FOUND,
} from "@/utils/constants";
import { focusItemCard } from "@/utils/focusItemCard";
import { ModalOverlay } from "@/components/modals/modalOverlay";
import { getItemId, itemIdRegex } from "@/utils/itemUrlUtils";
import { removeFromLocalStorage } from "@/utils/localStorageUtils";
import { getMatchingModifiers } from "@/utils/modifierUtils";
import {
  buildScanToOrderSearchParams,
  convertToScanToOrderInfo,
  extractScanToOrderParams,
  handleS2OTableData,
} from "@/utils/scanToOrderUtils";
import { LocalStorageKey } from "@/utils/storageKeys";
import { useItemRedirect } from "@/utils/useItemRedirect";
import { useTranslations } from "next-intl";
import { usePathname, useRouter } from "next/navigation";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useCookies } from "react-cookie";
import { RestaurantItemModal } from "./restaurantItemModal";

interface RestaurantMenuInteractionsProps {
  merchant: Merchant;
  menu: Menu | null;
  searchParams: { [key: string]: string | string[] | undefined };
  isAcceptingOrders: boolean;
  isEssentials?: boolean;
  selectedDayPart?: DayPart | null;
  initialTableData?: TableData | TableError | null;
  restaurantMfeEnabled?: boolean;
}

export const RestaurantMenuInteractions = ({
  merchant,
  menu,
  searchParams,
  isAcceptingOrders,
  isEssentials,
  selectedDayPart,
  initialTableData,
  restaurantMfeEnabled = false,
}: RestaurantMenuInteractionsProps) => {
  const { id: merchantId, merchantMeta, name: merchantName } = merchant;
  const { menuName, orderType, tableId, employeeId } = useMemo(
    () => extractScanToOrderParams(searchParams),
    [searchParams]
  );

  const t = useTranslations("RestaurantMenuInteractions");

  const isCheckoutEnabled = merchant.isCheckoutEnabled;
  const merchantColor =
    merchantMeta.colorPalette?.color || cloverColorTheme.Main.primaryColor;

  const [scanToOrderInfo] = useState(() =>
    convertToScanToOrderInfo(orderType, tableId, employeeId)
  );
  const router = useRouter();
  const pathname = usePathname();
  const [tableData, setTableData] = useState<TableData | TableError | null>(
    initialTableData || null
  );
  const [selectedItem, setSelectedItem] = useState<Item | null>(null);
  const [modifiers, setModifiers] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isClosingModal, setIsClosingModal] = useState<boolean>(false);
  const [, , removeCookie] = useCookies(["scanToOrderInfo"]);

  const getBaseRoute = (isEssential: boolean) =>
    isEssential ? ROUTE_CATEGORIES : ROUTE_MENU(restaurantMfeEnabled);

  let scanToOrderParams = "";
  if (scanToOrderInfo) {
    scanToOrderParams = buildScanToOrderSearchParams(scanToOrderInfo);
  }
  if (menuName) {
    scanToOrderParams += scanToOrderParams
      ? `&menu=${menuName}`
      : `?menu=${menuName}`;
  }

  const constructUrlAction = useCallback(
    (itemUrl: string) =>
      `${getBaseRoute(
        merchant.isEssentialMerchant
      )}/${itemUrl}${scanToOrderParams}`,
    [merchant.isEssentialMerchant, scanToOrderParams]
  );

  useItemRedirect({
    ignorePath: `${getBaseRoute(merchant.isEssentialMerchant)}/all`,
    constructUrlAction,
    items: menu?.items,
  });

  useEffect(() => {
    const tableId = scanToOrderInfo?.tableId;
    if (!tableId) {
      removeFromLocalStorage(LocalStorageKey.TABLE_DATA);
      removeFromLocalStorage(LocalStorageKey.SCAN_TO_ORDER_INFO);
      removeCookie("scanToOrderInfo", { path: "/" });
      return;
    }

    if (!initialTableData) return;

    handleS2OTableData(
      initialTableData,
      t("contactServer"),
      scanToOrderInfo,
      setTableData
    );
  }, [
    initialTableData,
    merchant.id,
    scanToOrderInfo,
    merchant.isRunningTabsEnabled,
  ]);

  useEffect(() => {
    const itemListPath = `${getBaseRoute(merchant.isEssentialMerchant)}/all`;
    if (pathname === itemListPath) {
      setIsModalOpen(false);
      setSelectedItem(null);
      return;
    }

    const itemId = getItemId(pathname);
    if (itemId) {
      if (!itemIdRegex.test(itemId)) {
        router.replace(ROUTE_NOT_FOUND);
      }
      const matchingItem = menu?.items.find((item) => item.id === itemId);
      if (matchingItem) {
        setSelectedItem(matchingItem);
        setIsModalOpen(true);
        const modifiers = getMatchingModifiers({
          modifiers: menu?.modifiers,
          item: matchingItem,
          modifierGroups: menu?.modifierGroups,
        });
        setModifiers(modifiers);
      }
    } else {
      setIsModalOpen(false);
      setSelectedItem(null);
    }
  }, [pathname, menu?.items]);

  let closeModalQueryParams = "";
  if (scanToOrderInfo) {
    closeModalQueryParams = buildScanToOrderSearchParams(scanToOrderInfo);
  }
  if (menuName) {
    closeModalQueryParams += closeModalQueryParams
      ? `&menu=${menuName}`
      : `?menu=${menuName}`;
  }
  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedItem(null);
    setIsClosingModal(true);

    router.replace(
      `${getBaseRoute(
        merchant.isEssentialMerchant
      )}/all${closeModalQueryParams}`,
      { scroll: false }
    );
  };

  useEffect(() => {
    const baseRoute = `${getBaseRoute(merchant.isEssentialMerchant)}/all`;

    if (pathname === baseRoute) {
      focusItemCard();
    }
  }, [pathname]);

  useEffect(() => {
    if (
      !isModalOpen &&
      isClosingModal &&
      pathname !== `${getBaseRoute(merchant.isEssentialMerchant)}/all`
    ) {
      const itemId = getItemId(pathname);

      if (itemIdRegex.test(itemId)) {
        router.replace(
          `${getBaseRoute(
            merchant.isEssentialMerchant
          )}/all${closeModalQueryParams}`,
          { scroll: false }
        );
      }
      if (pathname === `${getBaseRoute(merchant.isEssentialMerchant)}/all`) {
        setIsClosingModal(false);
      }
    }
  }, [
    isModalOpen,
    isClosingModal,
    closeModalQueryParams,
    merchant.isEssentialMerchant,
  ]);

  return (
    <>
      {tableData && (
        <WelcomeModalDisplay merchant={merchant} tableData={tableData} />
      )}
      {isModalOpen && selectedItem && (
        <RestaurantItemModal
          merchant={merchant}
          isCheckoutEnabled={isCheckoutEnabled}
          merchantName={merchantName}
          merchantColor={merchantColor}
          isOpen={isModalOpen}
          onRequestClose={closeModal}
          item={selectedItem}
          menu={menu}
          merchantId={merchantId}
          modifiers={modifiers}
          isAcceptingOrders={isAcceptingOrders}
          tableData={tableData}
          isRunningTabsEnabled={merchant.isRunningTabsEnabled}
          isEssentials={isEssentials}
          selectedDayPart={selectedDayPart}
        />
      )}
      {isModalOpen && <ModalOverlay />}
    </>
  );
};
